import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Avatar, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Paper, Switch, Typography } from "@mui/material";
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import JornadaService from "../../services/JornadaService";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IAlert from "../../types/Alert";
import { IAyudasJornada, IHorarioPersona, IJornadaConfig, IJornadaHorarios, IJornadaPersona } from "../../types/Jornada";
import { fechaCorta, lineasHorario, totalSemanal } from "../../utils/Jornada";
import NoDisponibleLectura from "./NoDisponibleLectura";
import ConfigUtils from "../../utils/Config";
import HorarioDialog from "./HorarioDialog";
import * as constantes from "../../common/Constantes";

// Pestaña Configuración (admin): quién ficha, su horario («Configurar horario» en cada fila), las ayudas para fichar y si se fichan
// las pausas. Los no disponibles se
// ven aquí en solo lectura; se gestionan en Configuración › Usuarios.
interface ConfiguracionJornadaProps {
    setAlert: (alert: IAlert) => void;
}

type Fallo = { message?: string; response?: { status?: number; data?: { message?: string } } };

const nombreRol = (role: number) => {
    const nombre = constantes.ROLES[role];
    return nombre ? nombre.charAt(0) + nombre.slice(1).toLowerCase() : '';
};

const unirNombres = (nombres: string[]) =>
    nombres.length <= 1 ? (nombres[0] ?? '') : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;

const ConfiguracionJornada = ({ setAlert }: ConfiguracionJornadaProps) => {
    const history = useNavigate();
    const [config, setConfig] = useState<IJornadaConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState<string | null>(null);
    // null hasta que llegan, para no avisar de «sin horario» antes de tiempo
    const [horarios, setHorarios] = useState<IHorarioPersona[] | null>(null);
    const [editando, setEditando] = useState<IJornadaPersona | null>(null);

    const tratarError = (e: unknown, metodo: string, porDefecto: string) => {
        const fallo = e as Fallo;
        if (fallo.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
            return;
        }
        if (fallo.response?.status !== 400 && fallo.response?.status !== 403 && fallo.response?.status !== 404) {
            const error: ILog = { method: `ConfiguracionJornada.${metodo}`, level: 'error', message: fallo.message ?? '', meta: fallo.response };
            LogService.logError(error);
        }
        setAlert({ open: true, severity: 'error', msg: fallo.response?.data?.message || porDefecto });
    };

    const cargarHorarios = () => {
        JornadaService.getHorarios()
            .then((response: { data: IJornadaHorarios }) => setHorarios(response.data.personas))
            .catch((e: unknown) => tratarError(e, 'getHorarios', 'No se han podido cargar los horarios'));
    };

    // La configuración y los horarios se piden a la vez y la pestaña no se pinta hasta tener los dos (sin saltos al llegar)
    useEffect(() => {
        Promise.all([JornadaService.getConfig(), JornadaService.getHorarios()])
            .then(([respConfig, respHorarios]: [{ data: IJornadaConfig }, { data: IJornadaHorarios }]) => {
                setConfig(respConfig.data);
                setHorarios(respHorarios.data.personas);
            })
            .catch((e: unknown) => tratarError(e, 'getConfig', 'No se ha podido cargar el registro de jornada'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cambiarFicha = (persona: IJornadaPersona, ficha: boolean) => {
        setGuardando(`persona-${persona.especialistaId}`);
        JornadaService.updatePersona(persona.especialistaId, ficha)
            .then(() => {
                setConfig(actual => actual && { ...actual, personas: actual.personas.map(p => p.especialistaId === persona.especialistaId ? { ...p, ficha } : p) });
                // Si es el propio admin, su pestaña Mi jornada aparece o desaparece sin volver a iniciar sesión
                if (persona.especialistaId === ConfigUtils.getUserInternalId()) {
                    ConfigUtils.setRegistroJornadaFicha(ficha);
                    window.dispatchEvent(new Event('popstate'));
                }
            })
            .catch((e: unknown) => tratarError(e, 'updatePersona', 'No se ha podido guardar'))
            .finally(() => setGuardando(null));
    };

    const cambiarAyuda = (clave: keyof IAyudasJornada, valor: boolean) => {
        if (!config) return;
        const ayudas = { ...config.ayudas, [clave]: valor };
        setGuardando('ayudas');
        JornadaService.updateAyudas(ayudas)
            .then(() => setConfig(actual => actual && { ...actual, ayudas }))
            .catch((e: unknown) => tratarError(e, 'updateAyudas', 'No se han podido guardar las ayudas'))
            .finally(() => setGuardando(null));
    };

    const cambiarPausas = (pausas: boolean) => {
        if (!config) return;
        setGuardando('pausas');
        JornadaService.updatePausas(pausas)
            .then(() => setConfig(actual => actual && { ...actual, pausas }))
            .catch((e: unknown) => tratarError(e, 'updatePausas', 'No se ha podido guardar'))
            .finally(() => setGuardando(null));
    };

    // Quien ficha y no tiene horario, ni vigente ni programado (se avisa encima de la lista)
    const sinHorario = horarios === null ? [] : (config?.personas ?? []).filter(p => {
        const horario = horarios.find(h => h.especialistaId === p.especialistaId);
        return p.ficha && !horario?.vigente && (horario?.programados.length ?? 0) === 0;
    });

    if (loading) return <CircularProgress />;
    if (!config) return null;
    if (!config.activo) {
        return (
            <Box>
                <Typography variant="body1">El registro de jornada está desactivado. Puedes activarlo en Configuración › Usuarios.</Typography>
                <Button variant="text" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => history(constantes.R_CONFIG)}>Ir a Configuración › Usuarios</Button>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Quién ficha y su horario</Typography>
            <Box component="ol" sx={{ mt: 1, mb: 1.5, pl: 3, color: 'text.secondary', typography: 'body2', '& li': { mb: 0.5 } }}>
                <li>Activa a cada persona que tiene que fichar. Por norma general, las personas con contrato laboral en la clínica.</li>
                <li>Pulsa «Configurar horario» para indicar sus días y horas de trabajo.</li>
            </Box>
            <Typography variant="body2" color="text.secondary">Si echas en falta a alguien, es porque no tiene acceso a la app: dáselo en Configuración › Usuarios.</Typography>

            {sinHorario.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {sinHorario.length <= 3
                        ? `${unirNombres(sinHorario.map(p => p.firstName))} ${sinHorario.length === 1 ? 'ficha pero no tiene' : 'fichan pero no tienen'} horario`
                        : `${sinHorario.length} personas fichan sin horario`}
                    : {sinHorario.length === 1 ? 'podrá' : 'podrán'} fichar, pero sin recordatorios ni comparación entre las horas previstas y las fichadas.
                </Alert>
            )}

            {/* Una tarjeta por persona */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                {config.personas.map(persona => {
                    const horario = horarios?.find(h => h.especialistaId === persona.especialistaId);
                    const rol = persona.esTitular ? 'Titular' : nombreRol(persona.role);
                    const tieneHorario = !!horario?.vigente || (horario?.programados.length ?? 0) > 0;
                    return (
                        <Paper variant="outlined" key={persona.especialistaId} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, px: 2, py: 1.5 }}>
                            {/* Iniciales con el color de la agenda */}
                            <Avatar sx={{ bgcolor: persona.color, width: 40, height: 40, fontSize: '0.95rem' }}>
                                {`${persona.firstName?.[0] ?? ''}${persona.lastName?.[0] ?? ''}`.toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{`${persona.firstName} ${persona.lastName ?? ''}`.trim()}</Typography>
                                {!persona.ficha
                                    ? <Typography variant="body2" color="text.secondary">{rol} · No ficha</Typography>
                                    : horario?.vigente
                                        ? <>
                                            <Typography variant="body2" color="text.secondary">{rol} · {totalSemanal(horario.vigente.dias)}</Typography>
                                            {/* Una línea por grupo de días con el mismo horario, con las horas alineadas */}
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, mt: 0.5 }}>
                                                {lineasHorario(horario.vigente.dias).map(linea => (
                                                    <Fragment key={linea.dias}>
                                                        <Typography variant="body2" color="text.primary">{linea.dias}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{linea.horas}</Typography>
                                                    </Fragment>
                                                ))}
                                            </Box>
                                        </>
                                        : <Typography variant="body2" color="text.secondary">{rol} · <Box component="span" sx={{ color: 'warning.main' }}>Sin horario</Box></Typography>}
                                {persona.ficha && horario?.programados.map(p => (
                                    <Chip key={p._id} size="small" variant="outlined" sx={{ mt: 0.5, mr: 0.5 }} label={`Cambio desde el ${fechaCorta(p.desde)}`} />
                                ))}
                                {/* No disponibles en solo lectura, aparte del horario */}
                                {(horario?.noDisponibles.length ?? 0) > 0 && (
                                    <Box sx={{ mt: 1, px: 1.5, py: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <EventBusyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>No disponible</Typography>
                                        </Box>
                                        {horario?.noDisponibles.map(n => <NoDisponibleLectura key={n._id} noDisponible={n} />)}
                                    </Box>
                                )}
                            </Box>
                            {/* Acciones arriba, a la altura del nombre. Solo tiene horario quien ficha */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                                {persona.ficha && (
                                    <Button size="small" variant={tieneHorario ? 'text' : 'outlined'} startIcon={<ScheduleRoundedIcon />} onClick={() => setEditando(persona)}>
                                        {tieneHorario ? 'Editar horario' : 'Configurar horario'}
                                    </Button>
                                )}
                                <FormControlLabel
                                    labelPlacement="start"
                                    label="Ficha"
                                    control={<Switch checked={persona.ficha} disabled={guardando !== null} onChange={(event) => cambiarFicha(persona, event.target.checked)} />}
                                />
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            <Typography variant="h6" sx={{ mt: 4, fontWeight: 600 }}>Ayudas para fichar</Typography>
            <Typography variant="body2" color="text.secondary">Avisos para que a nadie se le olvide fichar.</Typography>
            <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', mt: 1.5, px: 2, py: 1 }}>
                {[
                    { clave: 'banner' as const, titulo: 'Aviso en el Calendario', texto: 'Un aviso al entrar en el Calendario cuando toca fichar' },
                    { clave: 'badge' as const, titulo: 'Aviso en el icono', texto: 'Un punto en el icono de Jornada cuando toca fichar' },
                ].map(a => (
                    <FormControlLabel key={a.clave} sx={{ alignItems: 'flex-start', py: 0.5 }}
                        label={
                            <Box sx={{ pt: 1 }}>
                                <Typography variant="body1">{a.titulo}</Typography>
                                <Typography variant="body2" color="text.secondary">{a.texto}</Typography>
                            </Box>
                        }
                        control={<Checkbox checked={config.ayudas[a.clave]} disabled={guardando !== null} onChange={(event) => cambiarAyuda(a.clave, event.target.checked)} />}
                    />
                ))}
            </Paper>

            <Typography variant="h6" sx={{ mt: 4, fontWeight: 600 }}>Pausas</Typography>
            <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', mt: 1.5, px: 2, py: 1 }}>
                <FormControlLabel sx={{ alignItems: 'flex-start', py: 0.5 }}
                    label={
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="body1">Se fichan las pausas</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Añade «Iniciar pausa» en Mi jornada. Sin pausas, en turno partido se ficha la salida al mediodía y una nueva entrada por la tarde.
                            </Typography>
                        </Box>
                    }
                    control={<Checkbox checked={config.pausas} disabled={guardando !== null} onChange={(event) => cambiarPausas(event.target.checked)} />}
                />
            </Paper>

            <HorarioDialog
                open={editando !== null}
                especialistaId={editando?.especialistaId ?? null}
                nombre={editando ? `${editando.firstName} ${editando.lastName ?? ''}`.trim() : ''}
                onClose={() => setEditando(null)}
                onGuardado={cargarHorarios}
                setAlert={setAlert}
            />
        </Box>
    );
};

export default ConfiguracionJornada;
